import { useState, useEffect } from 'react';
import { Lock, Plus, Copy, Check, Trash2, Eye, Clock, Link2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';

interface PrivateLink {
  id: string;
  access_token: string;
  recipient_label: string;
  max_views: number | null;
  view_count: number;
  expires_at: string | null;
  is_active: boolean;
  last_viewed_at: string | null;
  created_at: string;
}

interface PrivateShareManagerProps {
  announcementId: string;
  publicSlug: string;
}

function getPrivateLinkUrl(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/a/private/${token}`;
}

export function PrivateShareManager({ announcementId, publicSlug }: PrivateShareManagerProps) {
  const { user } = useAuth();
  const [links, setLinks] = useState<PrivateLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // New link form state
  const [showForm, setShowForm] = useState(false);
  const [recipientLabel, setRecipientLabel] = useState('');
  const [expiryOption, setExpiryOption] = useState<string>('never');
  const [maxViews, setMaxViews] = useState<string>('unlimited');

  useEffect(() => {
    if (!user) return;
    fetchLinks();
  }, [user, announcementId]);

  const fetchLinks = async () => {
    const { data, error } = await supabase
      .from('private_share_links')
      .select('*')
      .eq('announcement_id', announcementId)
      .eq('user_id', user!.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setLinks(data as unknown as PrivateLink[]);
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!user || !recipientLabel.trim()) {
      toast.error('Please enter a recipient name');
      return;
    }

    setCreating(true);

    const expiresAt = (() => {
      const now = new Date();
      switch (expiryOption) {
        case '24h': return new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
        case '7d': return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
        case '30d': return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
        default: return null;
      }
    })();

    const maxViewsValue = maxViews === 'unlimited' ? null : parseInt(maxViews, 10);

    const { data, error } = await supabase
      .from('private_share_links')
      .insert({
        announcement_id: announcementId,
        user_id: user.id,
        recipient_label: recipientLabel.trim().slice(0, 100),
        expires_at: expiresAt,
        max_views: maxViewsValue,
      })
      .select()
      .single();

    if (error) {
      toast.error('Failed to create private link');
    } else if (data) {
      const newLink = data as unknown as PrivateLink;
      setLinks(prev => [newLink, ...prev]);
      toast.success('Private link created');

      // Auto-copy the new link
      const url = getPrivateLinkUrl(newLink.access_token);
      try {
        await navigator.clipboard.writeText(url);
        setCopiedId(newLink.id);
        setTimeout(() => setCopiedId(null), 2000);
        toast.success('Link copied to clipboard');
      } catch { }

      // Reset form
      setRecipientLabel('');
      setExpiryOption('never');
      setMaxViews('unlimited');
      setShowForm(false);
    }

    setCreating(false);
  };

  const handleCopy = async (link: PrivateLink) => {
    const url = getPrivateLinkUrl(link.access_token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
      toast.success('Private link copied');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleToggle = async (link: PrivateLink) => {
    const { error } = await supabase
      .from('private_share_links')
      .update({ is_active: !link.is_active })
      .eq('id', link.id);

    if (!error) {
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, is_active: !l.is_active } : l));
      toast.success(link.is_active ? 'Link deactivated' : 'Link reactivated');
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from('private_share_links')
      .delete()
      .eq('id', id);

    if (!error) {
      setLinks(prev => prev.filter(l => l.id !== id));
      toast.success('Link removed');
    }
  };

  const isExpired = (link: PrivateLink) => {
    return link.expires_at && new Date(link.expires_at) < new Date();
  };

  const isViewLimitReached = (link: PrivateLink) => {
    return link.max_views !== null && link.view_count >= link.max_views;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-foreground">Private Links</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowForm(!showForm)}
          className="gap-1.5 text-xs"
        >
          <Plus className="h-3.5 w-3.5" />
          New Private Link
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Create unique links for specific recipients with view limits and expiration.
      </p>

      {/* Create form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="glass border border-border/50 rounded-xl p-4 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Who is this link for?</Label>
                <Input
                  placeholder="e.g. Mom, David, Best Friend"
                  value={recipientLabel}
                  onChange={e => setRecipientLabel(e.target.value)}
                  maxLength={100}
                  className="text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Expires</Label>
                  <Select value={expiryOption} onValueChange={setExpiryOption}>
                    <SelectTrigger className="text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never</SelectItem>
                      <SelectItem value="24h">24 hours</SelectItem>
                      <SelectItem value="7d">7 days</SelectItem>
                      <SelectItem value="30d">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Max views</Label>
                  <Select value={maxViews} onValueChange={setMaxViews}>
                    <SelectTrigger className="text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unlimited">Unlimited</SelectItem>
                      <SelectItem value="1">1 view</SelectItem>
                      <SelectItem value="3">3 views</SelectItem>
                      <SelectItem value="10">10 views</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={handleCreate}
                disabled={creating || !recipientLabel.trim()}
                className="w-full gap-2 text-sm"
                size="sm"
              >
                <Lock className="h-3.5 w-3.5" />
                {creating ? 'Creating...' : 'Create & Copy Link'}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Existing links */}
      {links.length > 0 && (
        <div className="space-y-2">
          {links.map(link => {
            const expired = isExpired(link);
            const viewLimited = isViewLimitReached(link);
            const inactive = !link.is_active || expired || viewLimited;

            return (
              <div
                key={link.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  inactive
                    ? 'border-border/30 bg-muted/30 opacity-60'
                    : 'border-border/50 bg-card/50'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">
                      {link.recipient_label}
                    </span>
                    {expired && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-destructive border-destructive/30">
                        Expired
                      </Badge>
                    )}
                    {viewLimited && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-destructive border-destructive/30">
                        View limit
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {link.view_count}{link.max_views ? `/${link.max_views}` : ''} views
                    </span>
                    {link.expires_at && (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {expired ? 'Expired' : `Expires ${new Date(link.expires_at).toLocaleDateString()}`}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <Switch
                    checked={link.is_active}
                    onCheckedChange={() => handleToggle(link)}
                    className="scale-75"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleCopy(link)}
                    disabled={inactive}
                  >
                    {copiedId === link.id ? (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive/70 hover:text-destructive"
                    onClick={() => handleDelete(link.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && links.length === 0 && !showForm && (
        <div className="text-center py-3">
          <Link2 className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">
            No private links yet. Create one for recipient-only access.
          </p>
        </div>
      )}
    </div>
  );
}
